import type { ChartConfig } from '@/components/ui/chart'
import { Bar, BarChart, XAxis } from 'recharts'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { formatCurrencyFromDecimal } from '@/lib/format'
import { cn } from '@/lib/utils'

export interface SalesOverviewMonth {
  month: string
  transactions: number
  revenue?: number
}

export interface SalesOverviewPanelProps {
  type: 'revenue' | 'transactions'
  total?: number
  months: SalesOverviewMonth[]
  className?: string
}

const chartConfig = {
  value: {
    label: 'Value',
    color: 'var(--color-primary)',
  },
} satisfies ChartConfig

function formatMonthLabel(yearMonth: string): string {
  const date = new Date(`${yearMonth}-01`)
  return date.toLocaleString('nl-NL', { month: 'short' }).replace('.', '')
}

function formatMonthFull(yearMonth: string): string {
  const date = new Date(`${yearMonth}-01`)
  return date.toLocaleString('nl-NL', { month: 'long', year: 'numeric' })
}

function formatTotal(type: 'revenue' | 'transactions', total: number): string {
  if (type === 'revenue') {
    return formatCurrencyFromDecimal(total)
  }
  return new Intl.NumberFormat('nl-NL').format(total)
}

function SalesTooltipContent({
  active,
  payload,
  type,
}: {
  active?: boolean
  payload?: Array<{ payload: { monthFull: string; transactions: number; revenue?: number } }>
  type: 'revenue' | 'transactions'
}): React.ReactElement | null {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null
  }

  const data = payload[0]!.payload

  return (
    <div className="bg-background border-border/50 rounded-lg border px-3 py-2 text-xs shadow-xl">
      <p className="mb-1 font-medium capitalize">{data.monthFull}</p>
      <p className="text-muted-foreground">
        Transactions:{' '}
        <span className="text-foreground font-medium tabular-nums">
          {new Intl.NumberFormat('nl-NL').format(data.transactions)}
        </span>
      </p>
      {type === 'revenue' && data.revenue !== undefined && (
        <p className="text-muted-foreground">
          Revenue:{' '}
          <span className="text-foreground font-medium tabular-nums">
            {formatCurrencyFromDecimal(data.revenue)}
          </span>
        </p>
      )}
    </div>
  )
}

export function SalesOverviewPanel({
  type,
  total,
  months,
  className,
}: SalesOverviewPanelProps): React.ReactElement {
  const chartData = months.map((m) => ({
    monthLabel: formatMonthLabel(m.month),
    monthFull: formatMonthFull(m.month),
    transactions: m.transactions,
    revenue: m.revenue,
    value: type === 'revenue' ? (m.revenue ?? 0) : m.transactions,
  }))

  const subtitleFirstLine = type === 'revenue' ? 'Revenue' : 'Transactions'

  return (
    <div className={cn('bg-background flex flex-col rounded-xl p-7 shadow-lg', className)}>
      <h2 className="mb-5 text-3xl font-extrabold tracking-tight">Sales overview</h2>

      {total !== undefined && (
        <div className="mb-4 flex items-center gap-3">
          <span className="text-primary text-3xl font-extrabold tracking-tight">
            {formatTotal(type, total)}
          </span>
          <span className="text-muted-foreground text-sm leading-tight">
            {subtitleFirstLine}
            <br />
            the last 12 months
          </span>
        </div>
      )}

      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-40 w-full md:h-auto md:min-h-56 md:flex-1"
      >
        <BarChart data={chartData} barCategoryGap="12%">
          <XAxis
            dataKey="monthLabel"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            className="text-sm capitalize"
          />
          <ChartTooltip cursor={false} content={<SalesTooltipContent type={type} />} />
          <Bar
            dataKey="value"
            fill="var(--color-value)"
            fillOpacity={0.3}
            activeBar={{ fillOpacity: 1 }}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ChartContainer>
    </div>
  )
}
